import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { setTokens } from '@/shared/api/client';
import {
  fetchProfile,
  logout as logoutRequest,
  logoutEverywhere as logoutEverywhereRequest,
  type ConsumerProfile,
} from '@/entities/session/api';
import { clearOrderDraft } from '@/entities/order/draftStorage';
import { forgetPushToken } from '@/features/push/registration';

/**
 * Who is signed in, for the router and for every screen that shows account data.
 *
 * The profile is kept here rather than in React Query because the router's route guards
 * read it synchronously in `beforeLoad`, before any component renders. Query caching for
 * the profile would put that answer behind a promise and turn every guarded navigation
 * into a flash of the login screen.
 */
export interface AuthState {
  status: 'anonymous' | 'authenticated';
  profile: ConsumerProfile | null;
  /** Called after a successful login or registration — loads the profile and flips status. */
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Signs out every device (F2); the acting tab drops to the login screen just the same. */
  signOutEverywhere: () => Promise<void>;
  /** Re-reads the profile after something changed it (e.g. an account-deletion request). */
  reloadProfile: () => Promise<void>;
  /** Clears local state without calling the server — for a session that died on its own. */
  forgetSession: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({
  initialProfile,
  children,
}: {
  initialProfile: ConsumerProfile | null;
  children: ReactNode;
}) {
  const [profile, setProfile] = useState<ConsumerProfile | null>(initialProfile);

  /**
   * Bumped by every sign-out. A profile request in flight when the session ends would otherwise
   * land afterwards and set the profile back — reviving an account the server has already cut
   * loose, and bouncing the person off the login screen with tokens that no longer work.
   */
  const generation = useRef(0);

  /**
   * A restore that finished LATE still counts.
   *
   * `App` bounds the branded hold at `RESTORE_TIMEOUT_MS` and then mounts the router regardless —
   * an eternal splash is worse than a login screen. But the restore itself keeps going, and on a
   * phone it routinely lands a second or two after that deadline. Without this, `useState`'s
   * initial value had already been read and the arriving profile was dropped on the floor: a
   * person whose session was perfectly valid — the server had just answered `200` to both
   * `/auth/refresh` and `/consumer/me` — was left staring at the login form with no way back but
   * to type their password. Reproduced on a phone against the dev contour on 27.08.2026.
   *
   * Only null → profile is adopted. The reverse would let a stale `null` prop wipe a session the
   * person has since signed into by hand.
   */
  useEffect(() => {
    if (!initialProfile) return;
    setProfile((current) => current ?? initialProfile);
  }, [initialProfile]);

  const loadProfile = useCallback(async () => {
    const startedAt = generation.current;
    const me = await fetchProfile();
    if (generation.current !== startedAt) return;
    setProfile(me);
  }, []);

  /**
   * Every way out of a session funnels through here, which is why the push token is dropped here
   * and not in `signOut` alone. A session that died on its own (`forgetSession`) leaves the same
   * problem behind: a browser still subscribed under an account nobody is signed into, quietly
   * showing the next person's order updates to whoever picks up the phone.
   *
   * Deliberately not awaited. Revoking talks to the push service over the network, and a sign-out
   * that waits on it would hang on exactly the flaky connection that makes people sign out.
   *
   * The order draft goes at the same moment, and for a plainer reason: it holds a street address
   * and a note saying how to get into the car. Its 24-hour expiry is the backstop for a tab nobody
   * signs out of; signing out is the moment somebody is explicitly handing the browser back.
   */
  const endSession = useCallback(() => {
    generation.current += 1;
    setProfile(null);
    void forgetPushToken();
    clearOrderDraft();
  }, []);

  const forgetSession = useCallback(() => {
    setTokens(null, null);
    endSession();
  }, [endSession]);

  /** The person asked to leave, so the local session ends first and the call is best-effort. */
  const signOut = useCallback(async () => {
    endSession();
    await logoutRequest();
  }, [endSession]);

  /**
   * The opposite order, on purpose: this one only ends the local session once the server has
   * confirmed. A network failure here means the other devices are still signed in, and dropping
   * this session anyway would leave the person unable to retry the very thing they came to do.
   */
  const signOutEverywhere = useCallback(async () => {
    await logoutEverywhereRequest();
    endSession();
  }, [endSession]);

  const value = useMemo<AuthState>(
    () => ({
      status: profile ? 'authenticated' : 'anonymous',
      profile,
      signIn: loadProfile,
      signOut,
      signOutEverywhere,
      reloadProfile: loadProfile,
      forgetSession,
    }),
    [profile, loadProfile, signOut, signOutEverywhere, forgetSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth вызван вне AuthProvider');
  return value;
}
